// This is a list of all possible General routes. These functions return the URLs required for fetching data.

/**
 * Returns the URL for fetching dashboard data.
 * This URL is used by MancoDashboard and Dashboard components.
 *
 * @param {string} [raptor_platform_id] - Optional RaptorPlatform ID to filter dashboard data
 * @returns {string} The URL for fetching dashboard data.
 *
 * Used by MancoDashboard and Dashboard
 */
export function getDashboardDataURL(raptor_platform_id?: string) {
  const baseUrl = 'dashboard_data';
  if (raptor_platform_id) {
    return `${baseUrl}?raptor_platform_id=${raptor_platform_id}`;
  }
  return baseUrl;
}

/**
 * Constructs the URL for fetching funds based on the provided fund types and resources.
 *
 * @param {string} [fundTypes] - A comma-separated string of fund types.
 * @param {string} [rsResources] - A comma-separated string of resource identifiers.
 * @param {string} [raptorPlatformId] - The selected raptor platform ID.
 * @returns {string} The constructed URL for fetching funds.
 *
 * Used by useAppendOverviewFunds
 */

export const getFundsRoute = (
  fundTypes?: string,
  rsResources?: string[],
  raptorPlatformId?: string,
) => {
  const params: string[] = [];

  if (fundTypes?.length) params.push(`schemas=${fundTypes}`);
  if (rsResources?.length) params.push(`rs_resources=${rsResources}`);
  if (raptorPlatformId?.length)
    params.push(`raptor_platform_id=${raptorPlatformId}`);

  const queryString = params.length ? `?${params.join('&')}` : '';

  return `get_funds${queryString}`;
};

/**
 * Returns the URL for fetching reports list data.
 * This URL is used by various overview components to get available reports.
 *
 * @param {string} [fundTypes] - A comma-separated string of fund types.
 * @param {string} [reportType] - Optional specific report type (e.g., 'sub_fund_risk_report', 'esg_report', 'daily_liquidity_risk_report').
 * @param {string} [raptorPlatformId] - The selected raptor platform ID.
 * @returns {string} The URL for fetching reports list data.
 *
 * Used by OverviewPage, LiquidityOverview, EsgOverview, and other overview components
 */
export const getReportsListURL = (
  fundTypes?: string,
  reportType?: string,
  raptorPlatformId?: string,
  status?: string,
) => {
  const params: string[] = [];

  if (status?.length) params.push(`status=${status}`);
  if (fundTypes?.length) params.push(`schemas=${fundTypes}`);
  if (raptorPlatformId?.length)
    params.push(`raptor_platform_id=${raptorPlatformId}`);

  const queryString = params.length ? `?${params.join('&')}` : '';
  const baseUrl = reportType ? `reports_list/${reportType}` : 'reports_list';

  return `${baseUrl}${queryString}`;
};

/**
 * Returns the URL for fetching schema overview monitor data.
 * This URL is used by overview components to get risk monitoring data for a specific position date.
 *
 * @param {string} positionDate - The position date for which to fetch overview data.
 * @param {string} [fundTypes] - A comma-separated string of fund types.
 * @param {string} [raptorPlatformId] - The selected raptor platform ID.
 * @param {boolean} [deceased] - Whether to fetch deceased data.
 * @returns {string} The URL for fetching schema overview monitor data.
 *
 * Used by OverviewPage, LiquidityOverview, and other overview components
 */
export const getSchemaOverviewMonitorURL = (
  positionDate: string,
  fundTypes?: string,
  raptorPlatformId?: string,
  deceased?: boolean,
) => {
  const params: string[] = [];

  if (deceased) params.push(`status=deceased`);
  if (fundTypes?.length) params.push(`schemas=${fundTypes}`);
  if (raptorPlatformId?.length)
    params.push(`raptor_platform_id=${raptorPlatformId}`);

  const queryString = params.length ? `?${params.join('&')}` : '';

  return `schema_overview_monitor/${positionDate}${queryString}`;
};

export const getRiskOverviewMonitorURL = (
  positionDate: string,
  fundTypes?: string,
  raptorPlatformId?: string,
  deceased?: boolean,
) => {
  const params: string[] = [];

  if (deceased) params.push(`status=deceased`);
  if (fundTypes?.length) params.push(`schemas=${fundTypes}`);
  if (raptorPlatformId?.length)
    params.push(`raptor_platform_id=${raptorPlatformId}`);

  const queryString = params.length ? `?${params.join('&')}` : '';

  return `risk-monitor/${positionDate}${queryString}`;
};
